import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack,
  Share,
  CheckCircle,
  PictureAsPdf,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { paymentsAPI } from '../../services/api';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const TransactionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [transaction, setTransaction] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(null);
  const [sharing, setSharing] = useState(false);
  const receiptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        if (!id) return;
        const response = await paymentsAPI.getTransactionById(id);
        setTransaction(response.data.data);
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to load transaction');
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [id]);

  const handleShareClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareMenuAnchor(null);
  };

  const captureReceipt = async (): Promise<HTMLCanvasElement | null> => {
    if (!receiptRef.current) return null;
    try {
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#f5f5f5',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      return canvas;
    } catch (err) {
      console.error('Failed to capture receipt:', err);
      return null;
    }
  };

  const shareAsImage = async () => {
    handleShareClose();
    setSharing(true);
    try {
      const canvas = await captureReceipt();
      if (!canvas) return;
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], `MalPay_Receipt_${transaction?.tx_hash || id}.png`, { type: 'image/png' });
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: 'MalPay Receipt' });
        } else {
          // Fallback: download
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `MalPay_Receipt_${transaction?.tx_hash || id}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (err) {
      console.error('Share as image failed:', err);
    } finally {
      setSharing(false);
    }
  };

  const shareAsPDF = async () => {
    handleShareClose();
    setSharing(true);
    try {
      const canvas = await captureReceipt();
      if (!canvas) return;
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      const pdfBlob = pdf.output('blob');
      const file = new File([pdfBlob], `MalPay_Receipt_${transaction?.tx_hash || id}.pdf`, { type: 'application/pdf' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'MalPay Receipt' });
      } else {
        pdf.save(`MalPay_Receipt_${transaction?.tx_hash || id}.pdf`);
      }
    } catch (err) {
      console.error('Share as PDF failed:', err);
    } finally {
      setSharing(false);
    }
  };

  const SparkleIcon = () => (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: '#ffd700',
        position: 'absolute',
        animation: 'sparkle 2s infinite',
        '@keyframes sparkle': {
          '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
          '50%': { opacity: 1, transform: 'scale(1.2)' },
        },
      }}
    />
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Mobile App Bar */}
      <AppBar 
        position="static" 
        sx={{ 
          bgcolor: 'transparent', 
          boxShadow: 'none',
          px: 2,
          py: 1
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: 0 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: 'black', p: 1 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
            Transaction Details
          </Typography>
          <Box sx={{ width: 40 }} />
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ px: 2, pb: 4 }}>
        {/* Receipt area — captured for share */}
        <Box ref={receiptRef} sx={{ bgcolor: '#f5f5f5', pb: 2 }}>
        {/* Success Header */}
        <Box sx={{ textAlign: 'center', mb: 4, position: 'relative' }}>
          {/* Success Icon with Sparkles */}
          <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                bgcolor: '#4caf50',
                mx: 'auto',
                position: 'relative',
              }}
            >
              <CheckCircle sx={{ fontSize: 60, color: 'white' }} />
            </Avatar>
            
            {/* Sparkle Effects */}
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 20,
                height: 20,
              }}
            >
              <SparkleIcon />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                left: 10,
                width: 16,
                height: 16,
              }}
            >
              <SparkleIcon />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: 15,
                right: 20,
                width: 12,
                height: 12,
              }}
            >
              <SparkleIcon />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: 25,
                left: 15,
                width: 14,
                height: 14,
              }}
            >
              <SparkleIcon />
            </Box>
          </Box>

          {loading && <CircularProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && transaction && (
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: 'black', fontSize: { xs: '1.3rem', sm: '2.125rem' } }}>
              {transaction.type === 'receive'
                ? `You've received ₦${Number(transaction.amount).toLocaleString()}`
                : `You've sent ₦${Number(transaction.amount).toLocaleString()}`
              }
            </Typography>
          )}
        </Box>

        {/* Transaction Details Card */}
        <Card sx={{ borderRadius: 3, mb: 4, bgcolor: 'white' }}>
          <CardContent sx={{ p: 0 }}>
            {/* Date */}
            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'black' }}>
                  Date
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {transaction ? new Date(transaction.created_at || transaction.createdAt).toLocaleString() : '-'}
                </Typography>
              </Box>
            </Box>

            {/* Type */}
            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'black' }}>
                  Type
                </Typography>
                <Chip
                  label={transaction?.type || '-'}
                  sx={{
                    bgcolor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 'medium',
                  }}
                />
              </Box>
            </Box>

            {/* Sender */}
            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'black' }}>
                  Sender
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {transaction?.sender_name || transaction?.senderName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {transaction?.sender_email || transaction?.senderEmail || '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Recipient */}
            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'black' }}>
                  Recipient
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {transaction?.recipient_name || transaction?.recipientName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {transaction?.recipient_email || transaction?.recipientEmail || '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Status */}
            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'black' }}>
                  Status
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {transaction?.status || '-'}
                </Typography>
              </Box>
            </Box>

            {/* Nominal */}
            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'black' }}>
                  Nominal
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black' }}>
                  {transaction ? `₦${Number(transaction.amount).toLocaleString()}` : '-'}
                </Typography>
              </Box>
            </Box>

            {/* Settlement */}
            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'black' }}>
                  Settlement
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {transaction?.settlement_currency || transaction?.settlementCurrency
                    ? `${transaction.settlement_currency || transaction.settlementCurrency} ${Number(
                        transaction.settlement_amount || transaction.settlementAmount || 0
                      ).toLocaleString()}`
                    : 'NGN'}
                </Typography>
              </Box>
              {(transaction?.exchange_rate || transaction?.exchangeRate) && (
                <Typography variant="caption" color="text.secondary">
                  FX Rate: {Number(transaction.exchange_rate || transaction.exchangeRate).toLocaleString()}
                </Typography>
              )}
            </Box>

            {/* Fee */}
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'black' }}>
                  Fee
                </Typography>
                <Typography variant="body1" sx={{ color: '#4caf50', fontWeight: 'medium' }}>
                  {transaction?.total_fees || transaction?.totalFees
                    ? `₦${Number(transaction.total_fees || transaction.totalFees).toLocaleString()}`
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
              <Typography variant="body2" color="text.secondary">
                Paystack Fee: ₦{Number(transaction?.crypto_processor_fee || transaction?.cryptoProcessorFee || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                MalPay Fee: ₦{Number(transaction?.malpay_charge || transaction?.malpayCharge || 0).toLocaleString()}
              </Typography>
            </Box>
            {transaction?.failure_reason || transaction?.failureReason ? (
              <Box sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
                <Typography variant="body2" color="error">
                  {transaction.failure_reason || transaction.failureReason}
                </Typography>
              </Box>
            ) : null}
          </CardContent>
        </Card>

        </Box>{/* End receipt ref wrapper */}

        {/* Share Receipt Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={sharing ? <CircularProgress size={20} color="inherit" /> : <Share />}
          disabled={sharing || !transaction}
          onClick={handleShareClick}
          sx={{
            background: 'linear-gradient(135deg, #11C1EC, #09486D)',
            borderRadius: 3,
            py: 2,
            fontSize: '16px',
            fontWeight: 'bold',
            textTransform: 'none',
            boxShadow: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #0fb0d8, #07395a)',
              boxShadow: 4,
            },
          }}
        >
          {sharing ? 'Generating...' : 'Share Receipt'}
        </Button>
        <Menu
          anchorEl={shareMenuAnchor}
          open={Boolean(shareMenuAnchor)}
          onClose={handleShareClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          PaperProps={{
            sx: { borderRadius: 2, minWidth: 200 },
          }}
        >
          <MenuItem onClick={shareAsPDF}>
            <ListItemIcon><PictureAsPdf sx={{ color: '#e74c3c' }} /></ListItemIcon>
            <ListItemText>Share as PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={shareAsImage}>
            <ListItemIcon><ImageIcon sx={{ color: '#11C1EC' }} /></ListItemIcon>
            <ListItemText>Share as Image</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default TransactionDetailPage;
